import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, delay, map, Observable, of, retry, switchMap, } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { User } from '../user/user.types';
import * as Sentry from '@sentry/angular-ivy';
import { BaseApiService } from 'app/shared/utils/base-api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import posthog from "posthog-js";
import package_json from '../../../../package.json';

@Injectable()
export class AuthService extends BaseApiService {
    BASE_URL = 'https://curelinktech.in';
    private _authenticated: boolean = false;
    private _accessToken: string;


    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        matSnackBar: MatSnackBar
    ) {
        super(matSnackBar);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        try {
            this._accessToken = token;
            localStorage.setItem('accessToken', token);
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
        }
    }

    get accessToken(): string {
        let token: string;
        try {
            token = localStorage.getItem('accessToken') ?? '';
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
            token = this._accessToken ?? '';
        }
        return token;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _isUserAdmin(userID: string): boolean {
        return [
            '0cac4ff0-30a5-4b7e-a673-43944519cf5b', //Riya
            'c5c33132-32b8-464e-b22e-4a4f5c45a774', //Michelle
            '1ec2fe3d-6452-4a10-8661-0513c74039a2', //Kriti
            '857b71e8-40e7-4d1c-bf8b-96052ab2ed67', //Zishan
            '918f2b59-9b0b-4a3f-9c6d-393aa45ccf71', //Chat Team
            'c5a3f8a2-b000-49f2-938b-5caf247112c3', //Vardhan
            '88b6c686-dea0-4712-aef2-53fd93018cdf', //Tech
            '922932ee-bf60-4a8c-ba8a-21bc9f31743a', //Srijan
            'a7a6b35e-7617-4279-bfe5-70a020d3f26b', //Neethu
            'fae54312-428e-4bad-856c-0abcce6c3432', //Pravin
            '0d30b7e2-a477-45e6-8d49-7529001fdcfa', //Anushak
            '055f196f-d8aa-4a53-9436-f52cea5b8e9d', //Query Admin/John
            '7b43afba-0a14-48e1-975d-23b6921c7482', //Saatvik,
            '140c6894-b611-474e-83c8-8911eccd5495', //Sakshi Shrivastava
            '05573994-18be-401f-ac4c-2e4da13f7908', //Curelink Growth(Zoho)
        ].includes(userID);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient
            .get(`${this.BASE_URL}/users/detail/`, {
                headers: {
                    Authorization: `Token ${this.accessToken}`,
                },
            })
            .pipe(
                retry(3),
                delay(500),
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    // Return for non-approved users.
                    if (response === false) {
                        return of(false);
                    }
                    if (
                        response['body']['doctor_profile'][
                            'app_access_state'
                            ] != 2
                    ) {
                        return of(false);
                    }
                    // Return for Care Manager.
                    if (
                        response['body']['user']['id'] ===
                        '37e5c002-8b7c-4759-a745-fa847150cadc'
                    ) {
                        return of(false);
                    }
                    // Set the authenticated flag to true
                    this._authenticated = true;
                    // Store the user on the user service
                    this._userService.user = {
                        id: response['body']['user']['id'],
                        has_admin_access: this._isUserAdmin(
                            response['body']['user']['id']
                        ),
                        phone: response['body']['user']['phone'],
                        name: response['body']['doctor_profile']['name'],
                        photo: response['body']['doctor_profile']['photo'],
                        speciality_id: response['body']['doctor_profile']['speciality'],
                        email: response['body']['user']['email'],
                        token: this.accessToken,
                        is_available:
                            response['body']['doctor_profile']['is_available'],
                    } as User;
                    posthog.identify(response['body']['user']['id'], {
                        name: response['body']['doctor_profile']['name'],
                        phone: response['body']['user']['phone'],
                        release: package_json['version']
                    });
                    Sentry.setUser({
                        id: response['body']['user']['id'],
                        username: response['body']['doctor_profile']['name'],
                        email: response['body']['user']['email'],
                    });
                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.clear();
        sessionStorage.clear();

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     *
     * Sends an OTP to the given phone number.
     *
     * @param phone
     */
    sendOTP(phone: string): Observable<boolean> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/users/send_otp/`, {
                phone: `+91${phone}`,
                fcm_token: 'token',
            })
            .pipe(
                map((response: any) => {
                    return response['success'] as boolean;
                })
            );
    }

    /**
     *
     * Verifies the OTP and sets accessToken.
     *
     * @param phone
     * @param otp
     * @returns
     */
    verifyOTP(phone: string, otp: string): Observable<boolean> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/users/verify_otp/`, {
                phone: `+91${phone}`,
                otp: `${otp}`,
            })
            .pipe(
                map((response: any) => {
                    if (response['success']) {
                        this.accessToken = response['body']['token'];
                    }
                    return response['success'];
                })
            );
    }

    /**
     * Check the authentication status
     */
    checkAuthStatus(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // // Care Manager
        // if (this.accessToken === '869fca473e1673ba881733a35a435f07a2606ae0') {
        //     return of(false);
        // }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    /**
     * Check the admin access
     */
    checkAdminAccess(): Observable<boolean> {
        // Check if the user is logged in
        if (!this._authenticated) {
            return of(false);
        }

        return of(this._userService.user.has_admin_access);
    }
}
